import { useHydrateFileRecordEntity } from "@/api/fileRecords";
import { ChatFileRecordCardShell } from "@/components/ChatBubble/components/ChatFileRecordCardShell";
import { Skeleton } from "@/components/ui/skeleton";
import { ROUTES } from "@/constants/routes";
import { usePaneRouterContextSafe } from "@/context/PaneRouterContext";
import { useAppNavigate, useMainRouteParams } from "@/hooks/pane-routing";
import {
  useOpenFileRecord,
  useOpenLegacyEntityAsFileRecord,
} from "@/panes/file-record-detail";
import * as analytics from "@/utils/analytics";
import { route } from "@/utils/route";
import { Message } from "@cohesive/api-client";
import {
  ConnectionPayload,
  Entity,
  EntityCard,
  getEntityData,
  getEntityName,
  getEntityType,
  INSERT_CONNECTION_COMMAND,
  legacyFileEntityDataToFileRecord,
  MessageRenderer,
  SkillCardEntity,
} from "@cohesive/ui-components";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import React, { useCallback } from "react";
import { useNavigate } from "react-router";
import { ActionItemExecutionCardLive } from "../../ActionItemExecutionCardLive";

interface DefaultMessageRendererProps {
  message: Message;
  entities?: Map<string, Entity>;
}

function isArtifactEntity(entity: Entity): boolean {
  return "type" in entity && (entity as any).type === "artifact";
}

function isActionItemExecutionEntity(entity: Entity): entity is Entity & {
  type: "canvas_action_item_execution";
  data: { flow_session_id: string };
} {
  return (
    "type" in entity && (entity as any).type === "canvas_action_item_execution"
  );
}

function isSkillEntity(entity: Entity): entity is Entity & {
  type: "skill";
  data: {
    id: string;
    name: string;
    description?: string;
    icon?: any;
    status?: string;
  };
} {
  return "type" in entity && (entity as any).type === "skill";
}

/** Hydrates a wire file_record entity and renders via EntityCard. */
function InlineFileRecordCard({
  entity,
  flowSessionId,
}: {
  entity: Entity;
  flowSessionId: string;
}) {
  const openFileRecord = useOpenFileRecord();
  const {
    entity: hydrated,
    record,
    isLoading,
  } = useHydrateFileRecordEntity(
    "base" in entity && entity.base === "file_record"
      ? (entity as { base: "file_record"; data: unknown; platform: string })
      : null,
    { class: "flow-session", flow_session_id: flowSessionId },
  );
  if (isLoading)
    return <Skeleton className="h-32 w-full max-w-[320px] rounded-xl" />;
  if (!hydrated) return null;
  const id = String((hydrated as any).data?.id ?? "");
  return (
    // The shell gives the inline card the detail pane's kebab + right-click
    // menu (tier-aware: container file / reference / workspace file).
    <ChatFileRecordCardShell record={record}>
      {({ menuSlot }) => (
        <EntityCard
          entity={hydrated as Entity}
          onClick={() => openFileRecord(id, flowSessionId)}
          menuSlot={menuSlot}
        />
      )}
    </ChatFileRecordCardShell>
  );
}

const DefaultMessageRenderer: React.FC<DefaultMessageRendererProps> =
  React.memo(({ message, entities = new Map<string, Entity>() }) => {
    const [editor] = useLexicalComposerContext();
    const paneContext = usePaneRouterContextSafe();
    const navigate = useNavigate();
    const appNavigate = useAppNavigate();
    const { canvasId } = useMainRouteParams();
    const openLegacy = useOpenLegacyEntityAsFileRecord();
    const openFileRecord = useOpenFileRecord();

    // Note: For entity data enrichment, we rely on the entities being passed with
    // complete data from the parent component. The API fetching should happen at
    // a higher level where conditional hooks can be properly managed.
    // For now, we just pass through the entities as-is to MessageRenderer.

    // Legacy artifact entities resolve to a real file_record on the backend
    // (the migration script reuses the artifact UUID as the file_record id),
    // so we just route through the unified `useOpenFileRecord`.
    const openArtifact = useCallback(
      (artifactId: string) => {
        openFileRecord(artifactId, message.flow_session_id);
      },
      [openFileRecord, message.flow_session_id],
    );

    const openActionItemExecution = useCallback(
      (childSessionId: string) => {
        // Navigate to the child flow session in the right pane
        if (paneContext && !canvasId) {
          navigate(
            route(ROUTES.SESSIONS.SESSION, {
              sessionId: message.flow_session_id!,
            }),
            {
              state: {
                rightPane: {
                  pathname: route(ROUTES.SESSIONS.SESSION, {
                    sessionId: childSessionId,
                  }),
                },
              },
            },
          );

          return;
        }

        appNavigate({
          pane: "rightPane",
          to: route(ROUTES.SESSIONS.SESSION, { sessionId: childSessionId }),
        });
      },
      [paneContext, canvasId, navigate, message.flow_session_id, appNavigate],
    );

    // Route a detail pane open through the right pane. When the chat itself is
    // rendered inside a pane (legacy sessions page) we two-hop: bring the chat
    // into main and open the detail in the right pane. On canvas and the
    // full-page workspace chat the chat is already in main, so we open the
    // right pane directly. Mirrors `useOpenFileRecord`.
    const openInRightPane = useCallback(
      (detailPath: string, entity: Entity) => {
        if (paneContext && !canvasId && message.flow_session_id) {
          navigate(
            route(ROUTES.SESSIONS.SESSION, {
              sessionId: message.flow_session_id,
            }),
            {
              state: {
                rightPane: { pathname: detailPath, state: { entity } },
              },
            },
          );
          return;
        }

        appNavigate({
          pane: "rightPane",
          to: detailPath,
          options: { state: { entity } },
        });
      },
      [paneContext, canvasId, message.flow_session_id, navigate, appNavigate],
    );

    // Route a chat-message entity reference to its dedicated detail pane.
    // Mirrors the canvas EntityNode / openNodeInDetailPane routing so regular
    // chat (workspace + sessions) surfaces the same email / GitHub / Notion /
    // entity detail panes the canvas does, instead of silently no-op'ing.
    const openEntityDetail = useCallback(
      (entity: Entity) => {
        const entityData = getEntityData(entity);
        if (!entityData?.id) return;
        const entityId = entityData.id.toString();

        // Legacy image/video — adapt to a FileRecord and open the unified pane.
        if (
          "base" in entity &&
          (entity.base === "image" || entity.base === "video")
        ) {
          const record = legacyFileEntityDataToFileRecord(
            entity.base,
            entity.data as unknown as Record<string, unknown>,
          );
          if (record) openLegacy(record, message.flow_session_id);
          return;
        }

        const isNotionDocument =
          ("type" in entity &&
            (entity as any).type === "notion_mcp_document") ||
          ("base" in entity &&
            (entity as any).base === "document" &&
            entity.platform === "notion");
        if (isNotionDocument) {
          openInRightPane(
            route(ROUTES.CANVAS_EXPERIMENTS.NOTION_DETAIL, {
              pageId: entityId,
            }),
            entity,
          );
          return;
        }

        if (
          "base" in entity &&
          ((entity as any).base === "email" ||
            (entity as any).base === "email_thread") &&
          entity.platform === "gmail"
        ) {
          openInRightPane(
            route(ROUTES.CANVAS_EXPERIMENTS.EMAIL_DETAIL, {
              emailId: entityId,
            }),
            entity,
          );
          return;
        }

        if (
          "base" in entity &&
          entity.platform === "github" &&
          ((entity as any).base === "issue" ||
            ("type" in entity &&
              (entity as any).type === "github_project_item"))
        ) {
          openInRightPane(
            route(ROUTES.GITHUB.ISSUE_DETAIL, { issueId: entityId }),
            entity,
          );
          return;
        }

        if (
          "base" in entity &&
          entity.platform === "github" &&
          (entity as any).base === "pull_request"
        ) {
          openInRightPane(
            route(ROUTES.GITHUB.PR_DETAIL, { prId: entityId }),
            entity,
          );
          return;
        }

        // Legacy cohesive `canvas_file` uploads have no real detail pane.
        if (
          "base" in entity &&
          (entity as any).base === "file" &&
          ((entity as any).platform === "cohesive" ||
            (entity as any).type === "canvas_file")
        ) {
          return;
        }

        // Google Drive files — dedicated pane with native previews.
        if (
          "base" in entity &&
          entity.platform === "google-drive" &&
          (entity as any).base === "file"
        ) {
          openInRightPane(
            route(ROUTES.GOOGLE_DRIVE.FILE_DETAIL, { fileId: entityId }),
            entity,
          );
          return;
        }

        // Generic fallback — the unified entity detail pane.
        openInRightPane(
          route(ROUTES.ENTITIES.DETAIL, {
            platform: entity.platform || "cohesive",
            entityId,
          }),
          entity,
        );
      },
      [openLegacy, openInRightPane, message.flow_session_id],
    );

    // Always insert entity as mention into input
    const handleEntityMention = useCallback(
      (entity: Entity) => {
        const entityData = getEntityData(entity);
        if (!entityData) return;

        const connectionPayload = {
          platform: entity.platform,
          type: getEntityType(entity),
          identifier: entityData.id?.toString().replaceAll(":", "%3A"),
          title: getEntityName(entity),
        } as ConnectionPayload;

        editor.dispatchCommand(INSERT_CONNECTION_COMMAND, connectionPayload);

        analytics.trackConnectorEntitySelected({
          platform: connectionPayload.platform,
          entityType: connectionPayload.type,
          surface: "chat_message",
        });
      },
      [editor],
    );

    const openSkillDetail = useCallback(
      (skillId: string) => {
        appNavigate({
          pane: "rightPane",
          to: route(ROUTES.SKILLS.SHOW, { id: skillId }),
        });
      },
      [appNavigate],
    );

    // Custom entity renderer for action item executions (sub-agents)
    // ActionItemExecutionCardLive handles its own click navigation internally
    const renderEntity = useCallback(
      (entity: Entity) => {
        // Render action item executions with live status
        if (isActionItemExecutionEntity(entity)) {
          const entityData = getEntityData(entity);
          return (
            <ActionItemExecutionCardLive
              id={entityData.id}
              action_item_id={entityData.action_item_id}
              action_item_name={entityData.action_item_name}
              flow_session_id={entityData.flow_session_id}
              started={entityData.started}
              skip_reason={entityData.skip_reason}
            />
          );
        }

        // Render skill cards as compact inline card
        if (isSkillEntity(entity)) {
          const entityData = getEntityData(entity);
          return (
            <SkillCardEntity
              {...entityData}
              onClick={() => openSkillDetail(entityData.id)}
            />
          );
        }

        // Render file-record-backed entities (googleai_image/video, uploaded
        // attachments, etc.) by fetching the FileRecord and delegating to the
        // shared `FileRecordCard`. The entity payload only carries `{id,
        // title}` per the unified `base-file-record` schema, so everything
        // else — content type, presigned url, size — comes from the file
        // records API on demand.
        const baseValue = (entity as unknown as { base?: string }).base;
        if (baseValue === "file_record") {
          return (
            <InlineFileRecordCard
              entity={entity}
              flowSessionId={message.flow_session_id}
            />
          );
        }

        // Return null to use default rendering for other entity types
        return null;
      },
      [openSkillDetail, message.flow_session_id],
    );

    // Handle entity clicks - prioritize detail view for supported entities
    const handleEntityClick = useCallback(
      (entity: Entity) => {
        const entityData = getEntityData(entity);
        if (!entityData) return;

        // Handle artifacts: route to the unified file_record pane (the
        // migration reuses the artifact UUID as the file_record id).
        if (isArtifactEntity(entity)) {
          openArtifact(entityData.id.toString());
          return;
        }

        // Handle action item executions (sub-agents) by opening the child session
        if (isActionItemExecutionEntity(entity)) {
          openActionItemExecution(entityData.flow_session_id);
          return;
        }

        // Handle file entities - disable clicking (not clickable)
        // File entities from cohesive platform are not clickable
        if (
          entity.platform === "cohesive" &&
          "entity_type" in entity &&
          entity.entity_type === "file"
        ) {
          // File entities are not clickable - do nothing
          return;
        }

        openEntityDetail(entity);
      },
      [openArtifact, openActionItemExecution, openEntityDetail],
    );

    return (
      <MessageRenderer
        content={message.content}
        entities={entities}
        isStreaming={message.status === "streaming"}
        isAssistant={message.origin === "assistant"}
        onEntityClick={handleEntityClick}
        onEntityMentionClick={handleEntityMention}
        renderEntity={renderEntity}
      />
    );
  });

export default DefaultMessageRenderer;
